
import {
  FeatureSource,
  ModelBase,
  OptionalFeatureBase,
  SmartHash,
} from "@models"
import {
  Condition,
  EldritchInvocation,
  FightingStyle,
  Spell,
  SpellSlotType,
  Table,
  Item,
  MagicItemKeyword,
  PseudoItem,
  Feat,
  GameClass,
  Subclass,
  Gift,
  Background,
  PactBoon,
  Race,
  Subrace,
  CreatureInstance,
  Creature,
  Resource,
  SpecialFeature,
  Transform,
  Tool,
  WeaponMasteryProperty,
  SpellList,
  CreatureLimitation,
  RolledValue,
  CreatureGift,
  EquipmentPack,
  RollableTable,
  StandardFeatures,
  BastionFacility,
  CharacterOptions,
  Character,
  Campaign,
  DandDFeatureSource,
} from "@dndModels";
import {
  ALL_DRAGONS,
  CREATURE_TYPES,
  DATA_TYPE_INFO,
} from "@dndModels/Constants";
import { CONSTANTIZED_DATA_TYPES } from "@dndModels/DataTypeConstants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import { DataType } from "@dndModels/Types";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

const core_data_types: DataType[] = [
  "spell",
  "standard_features",
  "item",
  "race","subrace",
  "game_class","subclass",
  "background", 
  "table",
  // All of these are going to be removed from this.
  // Some are getting hardcoded (done)
  // Others are getting changed to loading as needed
  // "condition",
  // "optional_feature_base",
  // "spell_slot_type",
  // "transform",
  // "eldritch_invocation", 
  // "fighting_style",
  // "gift",
  // "pact_boon", 
  // "organization",
  // "org_position",
  // "adaptation",
  // "creature_gift",
  // I'll get to these ones later. Some I'm not sure I want to do, and the others aren't big deals.
  "tool",
  "equipment_pack",
  "magic_item_keyword",
  "feat",
  "special_feature",
  "resource", 
  "weapon_mastery_property",
  "rollable_table",
  "spell_list",
  "reusable_string"
];

export class DataUtilitiesClass {
  char_options: CharacterOptions | undefined = undefined;
  my_smart_hash: SmartHash = {};
  archived_data_holder: SmartHash = {};

  loadData = async ({ 
    char,
    onProgress,
    onError
  }: { 
    char: Character | Campaign, 
    onProgress?: ((data_type: DataType, percentage: number ) => void),
    onError?: ((message: string) => void)
  }) => {
    this.char_options = char.character_options;
    const char_options = this.char_options;
    if (char_options.allow_archives) {
      const archived_sb_ids = api.source_books.filter(o => 
        o.archived && char_options.source_books.includes(o._id)).map(o => o._id);
      const res = await api.getSetOfObjectsWithCallbacks({ 
        data_types: core_data_types, 
        archives: archived_sb_ids,
        onProgress, onError
      });
      const data_types = Object.keys(res);
      
      this.archived_data_holder = {};
      for (const data_type of data_types) {
        this.archived_data_holder[data_type] = res[data_type].filter(o => o.archived);
      }
    } else {
      // const res = 
      await api.getSetOfObjectsWithCallbacks({ 
        data_types: core_data_types, 
        onProgress, onError
      });
      // console.log("Got Objects");
      // console.log(res);
    }
    this.buildBasePseudoItems();
  }

  getAllDataOfType<T extends ModelBase>(data_type: DataType, log_it = false): T[] {
    if (log_it) {
      debugger;
    }
    if (this.my_smart_hash[data_type] && this.my_smart_hash[data_type].length > 0) {
      return this.my_smart_hash[data_type] as T[];
    } else {
      // if (data_type === "race") {
      //   debugger;
      // }
      if (api.smart_hash[data_type]) {
        let objects = [...api.smart_hash[data_type]];
        if (this.char_options) {
          const char_options = this.char_options;
          const source_book_ids = char_options.source_books;
          const blocked_ids = char_options.blocked;
          objects = objects.filter(o => 
            common_util.anyOverlaps(o.source_book_ids, source_book_ids) && 
            !blocked_ids.includes(o._id) && 
            !blocked_ids.includes(o.variant_group_id)
          );
          if (this.archived_data_holder[data_type]) {
            const archived = this.archived_data_holder[data_type];
            objects.push(...archived);
          }
          if (!char_options.world_settings.includes("Any")) {
            const world_settings = char_options.world_settings;
            objects = objects.filter(o => o.world_settings.length === 0 || common_util.anyOverlaps(world_settings, o.world_settings));
          }
        }
        this.my_smart_hash[data_type] = objects;
        return objects as T[];
      } 
    }
    return [];
  }

  getGoodDataOfType<T extends ModelBase>(data_type: DataType, log_it = false) {
    let objects = this.getAllDataOfType<T>(data_type, log_it);

    objects = this.getGood<T>(objects, data_type, log_it);
    return objects;
  }

  getGoodDataOfTypeAsync = async <T extends ModelBase>(data_type: DataType, log_it = false): Promise<T[]> => {
    if (core_data_types.includes(data_type)) {
      return this.getGoodDataOfType<T>(data_type, log_it);
    } else {
      let objects = await api.getObjects<T>({ data_type, log_it });

      objects = this.getGood<T>(objects, data_type, log_it);
      return objects;
    }
  }

  getGood = <T extends ModelBase>(objects: ModelBase[], data_type: DataType, log_it = false): T[] => {
    if (this.char_options) {
      const char_options = this.char_options;
      if (char_options.dominant_variants_only.length > 0) {
        const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
        if (log_it) {
          console.log(dt_info);
          if (dt_info) {
            console.log(dt_info.dominance_group);
            console.log(char_options.dominant_variants_only);
            console.log(char_options.dominant_variants_only.includes(dt_info.dominance_group));
          }
        }
        if (dt_info && char_options.dominant_variants_only.includes(dt_info.dominance_group)) {
          // if (dt_info.data_type === "race") {
          //   debugger;
          // }
          if (log_it) {
            console.log(objects);
            console.log(objects.map(o => o.true_name));
          }
          objects = objects.filter(o => o.non_version_variant || this.is_dominant(o));
          if (log_it) {
            console.log(objects);
            console.log(objects.map(o => o.true_name));
          }
        }
      }
      if (!char_options.allow_3rd_party) {
        objects = objects.filter(o => 
          !o.source_books_old.some(o2 => o2.third_party)
        );
      }
      if (!char_options.allow_UA) {
        objects = objects.filter(o => 
          !o.source_books_old.some(o2 => o2.type === "Unearthed Arcana")
        );
      }
      if (!char_options.allow_archives) {
        objects = objects.filter(o => 
          !o.archived
        );
      }
      if (!char_options.allow_homebrew) {
        objects = objects.filter(o => 
          !o.source_books_old.some(o2 => o2.type === "Homebrew")
        );
      }
      if (!char_options.allow_monster_feature_sources) {
        objects = objects.filter(o => 
          !o.monster
        );
      }
      if (!char_options.world_settings.includes("Any")) {
        const world_settings = char_options.world_settings;
        objects = objects.filter(o => o.world_settings.length === 0 || common_util.anyOverlaps(world_settings, o.world_settings));
      }
      const source_book_ids = char_options.source_books;
      const blocked_ids = char_options.blocked;
      // const removing = objects.filter(o => 
      //   !common_util.any_overlaps(o.source_book_ids, source_book_ids)
      // );
      // if (removing.length > 0) {
      //   // console.log("Removing");
      //   // console.log(removing);
      //   // console.log(source_book_ids);
      //   // for (const id of source_book_ids) {
      //   //   const sb = api.source_books.find(o => o._id === id);
      //   //   console.log(sb ? sb : `sb missing: ${id}`);
      //   // }
      //   // for (const r of removing) {
      //   //   console.log(r.source_book_ids);
      //   //   console.log(r.source_books);
      //   //   for (const id of r.source_book_ids) {
      //   //     const sb = api.source_books.find(o => o._id === id);
      //   //     console.log(sb ? sb : `sb missing: ${id}`);
      //   //   }
      //   // }
      //   // console.log(objects.filter(o => 
      //   //   blocked_ids.includes(o._id) 
      //   // ));
      //   // console.log(objects.filter(o => 
      //   //   blocked_ids.includes(o.variant_group_id)
      //   // ));
      //   // console.log(objects.filter(o => 
      //   //   !common_util.any_overlaps(o.source_book_ids, source_book_ids) || 
      //   //   blocked_ids.includes(o._id) || 
      //   //   blocked_ids.includes(o.variant_group_id)
      //   // ));
      // }
      objects = objects.filter(o => 
        common_util.anyOverlaps(o.source_book_ids, source_book_ids) && 
        !blocked_ids.includes(o._id) && 
        !blocked_ids.includes(o.variant_group_id)
      );
    }
    return objects as T[];
  }
  
  // Loaded ahead
  get spells() {
    return this.getGoodDataOfType("spell") as Spell[];
  }
  get all_spells() {
    return this.getAllDataOfType("spell") as Spell[];
  }
  get tables() {
    const tables = this.getGoodDataOfType("table") as Table[];
    return tables;
  }
  get items() {
    return this.getGoodDataOfType("item") as Item[];
  }
  get races() {
    return this.getGoodDataOfType("race") as Race[];
  }
  get subraces() {
    return this.getGoodDataOfType("subrace") as Subrace[];
  }
  get classes() {
    return this.getGoodDataOfType("game_class") as GameClass[];
  }
  get subclasses() {
    return this.getGoodDataOfType("subclass") as Subclass[];
  }
  get backgrounds() {
    return this.getGoodDataOfType("background") as Background[];
  }
  get standard_features() {
    return this.getGoodDataOfType("standard_features") as StandardFeatures[];
  }
  get pact_boons() {
    return this.getGoodDataOfType("pact_boon") as PactBoon[];
  }

  // Loaded as needed
  optional_feature_bases = async () => {
    return await api.getObjects({ data_type: "optional_feature_base" }) as OptionalFeatureBase[];
  }
  getConditions = async () => {
    return await this.getGoodDataOfTypeAsync("condition") as Condition[];
  }
  // This is just used in value() and encumbrance
  get conditions() {
    return this.getGoodDataOfType("condition") as Condition[];
  }
  getEldritchInvocations = async () => {
    return await this.getGoodDataOfTypeAsync("eldritch_invocation") as EldritchInvocation[];
  }
  getPactBoons = async () => {
    return await this.getGoodDataOfTypeAsync("pact_boon") as PactBoon[];
  }
  getFightingStyles = async () => {
    return await this.getGoodDataOfTypeAsync("fighting_style") as FightingStyle[];
  }
  getTransforms = async () => {
    return await this.getGoodDataOfTypeAsync("transform") as Transform[];
  }
  getGifts = async () => {
    return await this.getGoodDataOfTypeAsync("gift") as Gift[];
  }
  // This is just used in value()
  get gifts() {
    return this.getGoodDataOfType("gift") as Gift[];
  }
  getBastionFacilities = async () => {
    return await this.getGoodDataOfTypeAsync("bastion_facility") as BastionFacility[];
  }
  getSpellSlotTypes = async () => {
    return await this.getGoodDataOfTypeAsync("spell_slot_type") as SpellSlotType[];
  }
  // Only used in situations where we can be fairly certain that getSpellSlotTypes has already been run.
  get spell_slot_types() {
    return this.getGoodDataOfType("spell_slot_type") as SpellSlotType[];
  }
  get spell_lists() {
    return this.getGoodDataOfType("spell_list") as SpellList[];
  }
  getCreatureGifts = async () => {
    return await this.getGoodDataOfTypeAsync("creature_gift") as CreatureGift[];
  }
  get rollable_tables() {
    return this.getGoodDataOfType("rollable_table") as RollableTable[];
  }
  get equipment_packs() {
    return this.getGoodDataOfType("equipment_pack") as EquipmentPack[];
  }
  get magic_item_keywords() {
    return this.getGoodDataOfType("magic_item_keyword") as MagicItemKeyword[];
  }
  get feats() {
    return this.getGoodDataOfType("feat") as Feat[];
  }
  get special_features() {
    return this.getGoodDataOfType("special_feature") as SpecialFeature[];
  }
  get resources() {
    return this.getGoodDataOfType("resource") as Resource[];
  }
  get tools() {
    return this.getGoodDataOfType("tool") as Tool[];
  }
  get weapon_mastery_properties() {
    return this.getGoodDataOfType("weapon_mastery_property") as WeaponMasteryProperty[];
  }
  base_pseudo_items: PseudoItem[] = [];
  pseudo_items: PseudoItem[] = [];

  getTable = async (fs: FeatureSource) => { // }, edition_id: string) {
    if (fs instanceof DandDFeatureSource && fs.table.entries.length > 0) {
      return fs.table;
    } else {
      let table = this.tables.find(o => 
        o.type === fs.data_type && o.for_id === fs._id);
      if (!table) {
        if (!fs.is_true_dominant) {
          // Look for one from the dominant one
          const dominant = fs.variant_group.find(o => o.is_true_dominant);
          if (dominant) {
            table = this.tables.find(o => 
              o.type === fs.data_type && o.for_id === dominant._id);
          }
        }
        if (!table) {
          // We still don't have a table. Look for one from the original.
          const original = fs.variant_group.find(o => o._id === o.variant_group_id);
          if (original) {
            table = this.tables.find(o => 
              o.type === fs.data_type && o.for_id === original._id);
          }
        }
      }
      if (table) {
        await this.loadObject(table);
      }
      return table;
    }
  }

  loadObject = async (obj: ModelBase) => {
    await api.loadObject(obj);
  }

  get all_feature_sources(): FeatureSource[] {
    let eis: EldritchInvocation[] = [];
    if (this.my_smart_hash.eldritch_invocation) {
      eis = this.my_smart_hash.eldritch_invocation as EldritchInvocation[];
    }
    let pbs: PactBoon[] = [];
    if (this.my_smart_hash.pact_boon) {
      pbs = this.my_smart_hash.pact_boon as PactBoon[];
    }
    let gifts: Gift[] = [];
    if (this.my_smart_hash.gift) {
      gifts = this.my_smart_hash.gift as Gift[];
    }
    let special_features: SpecialFeature[] = [];
    if (this.my_smart_hash.special_feature) {
      special_features = this.my_smart_hash.special_feature as SpecialFeature[];
    }
    let feats: Feat[] = [];
    if (this.my_smart_hash.feat) {
      feats = this.my_smart_hash.feat as Feat[];
    }
    let magic_item_keywords: MagicItemKeyword[] = [];
    if (this.my_smart_hash.magic_item_keyword) {
      magic_item_keywords = this.my_smart_hash.magic_item_keyword as MagicItemKeyword[];
    }
    let fighting_styles: FightingStyle[] = [];
    if (this.my_smart_hash.fighting_style) {
      fighting_styles = this.my_smart_hash.fighting_style as FightingStyle[];
    }
    return [
      ...this.backgrounds, ...this.races, ...this.subraces, 
      ...this.items, ...this.classes, ...this.subclasses,
      ...special_features, ...feats, ...magic_item_keywords, 
      ...eis, ...pbs, ...gifts,
      ...fighting_styles
    ];
  }

  // pseudo_items_built: boolean = false; // This flag is to just make sure it did this.  A weird bug has popped up that is making it so it's not hitting that part of the code when it should.  It's skipping it and then hitting it later.
  buildBasePseudoItems() {
    // console.log("Building Pseudo Items");
    // Goes through this.items, copying them all into this.base_pseudo_items,
    // but if the item uses extras then it does it for each extra.
    // Change everything that uses this.items to use this.base_pseudo_items instead
    this.base_pseudo_items = [];
    for (const item of this.items) {
      if (item.extra_base_type === "" || item.extra_base_type === "None") {
        const pseudo_item = new PseudoItem(item);
        this.base_pseudo_items.push(pseudo_item);
      } else {
        if (item.extra_base_type === "Spell") {
          const extra_base_limits = item.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] };
          let spell_levels: number[] = [];
          if (extra_base_limits.levels.includes("Any")) {
            spell_levels = [0,1,2,3,4,5,6,7,8,9];
          } else {
            spell_levels = extra_base_limits.levels.map(o => {
              if (o === "Cantrip") {
                return 0;
              } else {
                return +o;
              }
            });
          }
          const spells = this.spells.filter(o => 
            spell_levels.includes(o.level) && 
            (extra_base_limits.schools.includes("Any") || 
              extra_base_limits.schools.includes(o.school)));
          for (const spell of spells) {
            const pseudo_item = new PseudoItem(item, spell._id, spell);
            this.base_pseudo_items.push(pseudo_item);
            // const new_pseudo_item = pseudo_item.clone();
            // new_pseudo_item.add_keyword(item, spell._id, spell);
            // pseudo_items.push(new_pseudo_item);
          }
        } else if (item.extra_base_type === "Dragon Breed") {
          const extra_base_limits = item.extra_base_limits;
          const dragon_breeds = extra_base_limits.length > 0 ? extra_base_limits : ALL_DRAGONS;
          for (const breed of dragon_breeds) {
            const pseudo_item = new PseudoItem(item, breed);
            this.base_pseudo_items.push(pseudo_item);
            // const new_pseudo_item = pseudo_item.clone();
            // new_pseudo_item.add_keyword(item, breed);
            // pseudo_items.push(new_pseudo_item);
          }
        } else if (item.extra_base_type === "Creature Type") {
          const extra_base_limits = item.extra_base_limits;
          const creature_types = extra_base_limits.length > 0 ? extra_base_limits : CREATURE_TYPES;
          for (const c_type of creature_types) {
            const pseudo_item = new PseudoItem(item, c_type);
            this.base_pseudo_items.push(pseudo_item);
            // const new_pseudo_item = pseudo_item.clone();
            // new_pseudo_item.add_keyword(item, c_type);
            // pseudo_items.push(new_pseudo_item);
          }
        } else if (item.extra_base_type === "Damage Type") {
          console.error("Not Yet Implemented");
        } else if (item.extra_base_type === "Strings") {
          console.error("Not Yet Implemented");
        } else {
          console.error("Not Yet Implemented");
          debugger;
        }
      }
    }
    // this.pseudo_items_built = true;
  }

  getObjects = async (type: string, filter: any = {}) => {
    let return_us: ModelBase[] = [];
    switch (type) {
      case "condition": case "Condition": return_us = await this.getConditions(); break;
      case "spell": case "Spell": return_us = this.spells; break;
      case "eldritch_invocation": case "Eldritch Invocation": return_us = await this.getEldritchInvocations(); break;
      case "spell_slot_type": case "Spell Slot Type": return_us = await this.getSpellSlotTypes(); break;
      case "fighting_style": case "Fighting Style": return_us = await this.getFightingStyles(); break;
      case "base_item": case "Base Item": case "item": case "Item": return_us = this.items; break;
      // case "magic_item": case "Magic Item": return_us = this.magic_items; break;
      case "magic_item_keyword": case "Magic Item Keyword": return_us = this.magic_item_keywords; break;
      case "feat": case "Feat": return_us = this.feats; break;
      case "table": case "Table": return_us = this.tables; break;
      case "special_feature": case "Special Feature": return_us = this.special_features; break;
      case "gift": case "Gift": return_us = await this.getGifts(); break;
      case "race": case "Race": return_us = this.races; break;
      case "subrace": case "Subrace": return_us = this.subraces; break;
      case "game_class": case "Class": return_us = this.classes; break;
      case "subclass": case "Subclass": return_us = this.subclasses; break;
      case "background": case "Background": return_us = this.backgrounds; break;
      case "pact_boon": case "Pact Boon": return_us = await this.getPactBoons(); break;
      case "resource": case "Resource": return_us = this.resources; break; 
      case "tool": case "Tool": return_us = this.tools; break;
      case "transform": case "Transform": return_us = await this.getTransforms(); break;
      // case "adaptation": case "Adaptation": return_us = await this.getAdaptations(); break;
      case "bastion_facility": case "Bastion Facility": return_us = await this.getBastionFacilities(); break;
      case "weapon_mastery_property": return_us = this.weapon_mastery_properties; break;
    }
    if (Object.keys(filter).length > 0) {
      return_us = api.filter("", return_us, filter); // , type.includes("ransform"));
    }
    return return_us;
  }
  
  getCreatureGiftsForCreature = async (creature: CreatureInstance) => {
    const creature_gifts = await this.getCreatureGifts();
    for (const cg of creature_gifts) {
      await this.loadObject(cg);
    }
    return creature_gifts.filter(o => this.fitCreatureLimit(creature, o.limit) || creature.creature_gifts.filter(o2 => o2.gift_id === o._id).length > 0);
  }

  fitCreatureLimit(ci: CreatureInstance | Creature, limit: CreatureLimitation, log_it = false): boolean {
    const creature = ci instanceof CreatureInstance ? ci.creature : ci;
    if (creature) {
      // log_it = item.name.includes("Holding"); // true;
      if (log_it) {
        console.log(limit);
        console.log(creature);
      }
      if (limit.creature_ids.length > 0) {
        return limit.creature_ids.includes(creature._id);
      } 
      let good = true;
      if (good && limit.creature_types.length > 0) {
        good = limit.creature_types.includes(ci.creature_type);
      } 
      if (good && limit.subtypes.length > 0) {
        good = common_util.anyOverlaps(limit.subtypes, ci.subtypes);
      } 
      if (good && limit.age_categories.length > 0) {
        good = limit.age_categories.includes(creature.age_category_for_cr(ci.challenge_rating));
      } 
      if (good && limit.size_nums.length > 0) {
        good = limit.size_nums.includes(creature.size_num);
      } 
      if (good && limit.min_challenge_rating > -1) {
        good = limit.min_challenge_rating <= creature.max_challenge_rating;
      } 
      if (good && limit.max_challenge_rating > -1) {
        good = limit.max_challenge_rating >= creature.min_challenge_rating;
      } 
      if (good && !limit.allow_flight) {
        good = !creature.flying;
      }
      if (good && !limit.allow_swimming) {
        good = !creature.swimming;
      }
      return good;
    }
    return false;
  }

  getSpell(id: string) {
    let obj_finder = this.spells.find(o => o._id === id);
    if (!obj_finder) {
      // It's probably archived, and we're not including it.  
      // Look for a dominant variant.
      obj_finder = this.getDominantSpell(id); // this.spells.filter(o => o.variant_ids.includes(char_spell.spell_id) && this.is_dominant(o));
    }
    return obj_finder;
  }

  getDominantSpell(id: string) {
    let obj_finder = this.spells.find(o => o._id === id);
    if (!obj_finder) {
      obj_finder = this.spells.find(o => o.variant_ids.includes(id));
    }
    if (obj_finder) {
      // Check for a dominant variant
      const dominant_variant = (obj_finder.variant_group as Spell[]).find(o => this.is_dominant(o));
      if (dominant_variant) {
        obj_finder = dominant_variant;
      }
    }
    return obj_finder;
  }

  getSpellVariants(id: string) {
    return this.spells.filter(o => o._id === id || o.variant_ids.includes(id));
  }

  spellOnASpellList(spell_id: string, spell_list_ids: string[]) {
    const spell = this.spells.find(o => o._id === spell_id);
    if (spell) {
      if (spell_list_ids.length === 0 || spell_list_ids.includes("Any")) {
        return true;
      }
      return this.spell_lists.filter(o => 
        spell_list_ids.includes(o._id) && 
        api.fitSpellLimit(spell, o.limit)
      ).length > 0;
    }
    return false;
  }

  totalRolledValue(rolled_values: RolledValue[]) {
    let total = 0;
    for (const rvs of rolled_values) {
      for (const rv of rvs.roll_values) {
        for (const v of rv.main_values) {
          total += v;
        }
      }
      total += rvs.flat;
    }
    return total;
  }

  getStringWithReplacements3({
    clean_me, 
    backup = "{obj.name}", 
    obj,
    // api,
    constants
  }: {
    clean_me: string, 
    backup?: string, 
    obj: any,
    // api?: APIClass,
    constants?: any
  }): string {
    // if (obj instanceof Subclass && obj.name.includes("Aberrant")) {
    //   debugger;
    // }
    const obj_as_any = obj as any;
    const getValue = (field: any) => {
      const value = obj_as_any[field];
      if (value !== undefined) {
        return value;
      }
      // console.error("get_string_with_replacements_model_base error");
      // debugger;
      return "VALUE NOT FOUND!";
    }
    const clean_it = (clean_me2: string) => {
      while (clean_me2.includes("{obj.")) {
        // There's something besides the name that needs replacing.
        let index = clean_me2.indexOf("{obj.");
        const after = clean_me2.substring(index + 5);
        index = after.indexOf("}");
        const field = after.substring(0, index);
        const value = getValue(field); 
        clean_me2 = common_util.replaceAll(clean_me2, `{obj.${field}}`, `${value}`);
      }
      if (constants) {
        while (clean_me2.includes("{constants_map.")) {
          // There's something besides the name that needs replacing.
          let index = clean_me2.indexOf("{constants_map.");
          const after = clean_me2.substring(index + 15);
          index = after.indexOf("}");
          const stuff = after.substring(0, index);
          index = stuff.indexOf("[");
          const map_name = stuff.substring(0, index);
          const sub = stuff.substring(index + 1);
          index = sub.indexOf("]");
          const key = sub.substring(0, index);
          const field = sub.substring(index + 2);
          let value = "";
          try {
            if (map_name.length > 0 && key.length > 0) {
              if (field.length > 0) {
                value = constants[map_name][key][field];
              } else {
                value = constants[map_name][key];
              }
            }
          } catch (ex) {}
          clean_me2 = common_util.replaceAll(clean_me2, `{constants_map.${stuff}}`, `${value}`);
        }
      }
      if (api) {
        while (clean_me2.includes("{list_of_type:")) {
          // There's something besides the name that needs replacing.
          // {list_of_type:data_type;[ids]}
          // {list_of_type:data_type;display_field;[ids]}
          let index = clean_me2.indexOf("{list_of_type:");
          const after = clean_me2.substring(index + 14);
          index = after.indexOf("}");
          const stuff = after.substring(0, index);
          index = stuff.indexOf(";");
          const data_types = stuff.substring(0, index).split(",") as DataType[];
          // if (data_types.length > 1) {
          //   debugger;
          // }
          let ids = stuff.substring(index + 1);
          let value = "";
          let display_field = "";
          if (ids.indexOf(";") > -1) {
            // has a display_field specified.
            index = ids.indexOf(";");
            display_field = ids.substring(0, index);
            ids = ids.substring(index + 1);
          } 
          const use_ids = common_util.replaceAll(common_util.replaceAll(ids, "[", ""), "]", "").split(",").map(o => o.trim());
          let unused_ids = [...use_ids];
          for (const data_type of data_types) {
            let value_for_dt = "";
            try {
              let objects: ModelBase[] = [];
              if (api.smart_hash[data_type]) {
                objects = api.smart_hash[data_type];
              } else if (CONSTANTIZED_DATA_TYPES.includes(data_type)) {
                objects = api.getConstantizedObjects({ data_type });
              }
               
              objects = objects.filter(o => unused_ids.includes(o._id));
              if (display_field === "") {
                value_for_dt = objects.map(o => o.name).join(", ");
              } else {
                value_for_dt = (objects as any[]).map(o => o[display_field]).join(", ");
              }
              if (value_for_dt.length > 0) {
                value += (value.length > 0 ? ", " : "") + value_for_dt;
              }
              unused_ids = unused_ids.filter(o => !objects.some(o2 => o2._id === o));
            } catch (ex) {}
          }
          if (unused_ids.length > 0) {
            value += (value.length > 0 ? ", " : "") + unused_ids.join(", ");
          }
          clean_me2 = common_util.replaceAll(clean_me2, `{list_of_type:${stuff}}`, `${value}`);
        }
      }
      return clean_me2;
    }
    let cleaned = clean_it(clean_me); 
    if (cleaned.includes("VALUE NOT FOUND!") || cleaned === "") {
      if (clean_me === backup && cleaned !== "") {
        cleaned = clean_it("{obj.name}");
      } else {
        cleaned = clean_it(backup); 
      }
    }

    return cleaned;
  }

  variant_group(obj: ModelBase) {
    return this.getAllDataOfType(obj.data_type).filter(o => o.variant_group_id === obj.variant_group_id);
  }

  is_dominant(obj: ModelBase, log_it = false) {
    if (log_it) {
      debugger;
    }
    if (obj.non_version_variant) {
      return false;
    } else {
      const variant_group = this.variant_group(obj);
      const max_dominance_level = Math.max(...variant_group.map(o => o.dominance_level));
      if (log_it) {
        console.log(variant_group);
        console.log(max_dominance_level);
      }
      if (max_dominance_level === 0) {
        return obj._id === obj.variant_group_id;
      } else {
        return obj.dominance_level === max_dominance_level;
      }
    }
  }

  renderChallengeRating(challenge_rating: number) {
    if (challenge_rating > 0) {
      if (challenge_rating >= 1) {
        return ` (CR: ${challenge_rating})`;
      } else if (challenge_rating === 0.125) {
        return ` (CR: 1/8)`;
      } else if (challenge_rating === 0.25) {
        return ` (CR: 1/4)`;
      } else if (challenge_rating === 0.5) {
        return ` (CR: 1/2)`;
      }
    }
    return "";
  }

  variant_of_name(obj: ModelBase) {
    // if (this.name.includes("Aberrant")) {
    //   console.log(this.variant_group);
    // }
    const variant_group = this.variant_group(obj);
    const dominant_variant = variant_group.find(o => o.is_true_dominant);
    if (dominant_variant && dominant_variant._id !== obj._id) {
      return dominant_variant.name;
    }
    return "";
  }

  getVariantGroupId(data_type: DataType, id: string) {
    const data = this.getGoodDataOfType(data_type);
    if (data.length > 0) {
      let obj = data.find(o => o._id === id);
      if (!obj) {
        obj = data.find(o => o.variant_ids.includes(id));
      }
      if (obj) {
        return obj.variant_group_id;
      }
    }
    return "";
  }

  getVariantGroupIds(data_type: DataType, ids: string[]) {
    return ids.map(id => this.getVariantGroupId(data_type, id));
  }
}
